import { motion } from "framer-motion";

export default function DossierCard() {
  return (
    <section
      data-testid="dossier-section"
      className="relative py-12 lg:py-16 border-t border-white/[0.06] overflow-hidden"
    >
      <div className="absolute inset-0 dossier-hatch opacity-50 pointer-events-none" />

      <div className="relative max-w-[1120px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-[760px] mb-16"
        >
          <div className="font-mono-label text-[#D4FF4A]/70 mb-6">
            Example / your rich customer avatar
          </div>
          <h2
            data-testid="dossier-heading"
            className="font-display text-white text-[38px] lg:text-[52px] font-semibold mb-6"
          >
            Meet your ideal customer.
          </h2>
          <p className="text-lg text-[#A1A1AA] leading-[1.55]">
            A useful customer profile built from the patterns inside your highest-value buyer segment.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative bg-[#0A0A0F] border border-white/[0.08] rounded-2xl overflow-hidden"
          data-testid="dossier-card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr]">
            <div className="relative aspect-[4/5] lg:aspect-auto bg-[#11111C] overflow-hidden border-b lg:border-b-0 lg:border-r border-white/[0.06]">
              <img
                src="/images/marcus-delgado.png"
                alt="Marcus Delgado, agency owner and #1 ranked buyer"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F]/80 via-[#0A0A0F]/20 to-[#0A0A0F]/40 pointer-events-none" />
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                <div className="font-mono-label text-[#D4FF4A]/90 text-[10px] tracking-[0.2em]">
                  Illustrative profile
                </div>
                <div className="w-7 h-7 rounded-full border border-[#D4FF4A]/40 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D4FF4A]" />
                </div>
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-mono-label text-[#D4FF4A]/80 text-[10px] mb-1">
                  Subject · Core persona
                </div>
                <div className="font-display text-white text-2xl font-semibold mb-1">
                  Marcus Delgado
                </div>
                <div className="text-[#A1A1AA] text-sm">
                  Agency owner · Northbound Studio
                </div>
              </div>
            </div>

            <div className="p-8 lg:p-10 space-y-8">
              <div className="grid grid-cols-2 gap-6 text-sm">
                <Field label="Location" value="Austin, TX" />
                <Field label="Role" value="Founder / Agency Owner" />
                <Field label="LTV rank" value="Top customer segment" />
                <Field label="Revenue contribution" value="High value" />
                <Field label="Cluster" value="Agency Owner" />
                <Field label="Archetype" value="The Margin Maximizer" />
              </div>

              <Divider />

              <Block title="What he reads">
                Demand Curve · Marketing Against The Grain · Exit Five · Marketing Examples ·
                SparkToro
              </Block>

              <Block title="Where he decides">
                G2 shortlist · Reddit r/agency · YouTube walkthroughs (Spencer Haws, Ed Leake) ·
                Private Slack: Agency Founders
              </Block>

              <Divider />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Block title="Words he uses">
                  <TagRow words={["Margin", "Retainer", "Scope creep", "White label", "Ops-light"]} />
                </Block>
                <Block title="Words he doesn't">
                  <TagRow
                    words={["Disruptive", "AI-first", "Enterprise-grade", "Synergies"]}
                    muted
                  />
                </Block>
              </div>

              <Divider />

              <Block title="3AM wake-up thought">
                <blockquote className="text-white text-[17px] leading-[1.55] italic border-l-2 border-[#D4FF4A] pl-5">
                  "If I lose the Henderson retainer next quarter, I've got 45 days of runway. I
                  need to land two more like them, and I'm not sure the pipeline is there yet."
                </blockquote>
              </Block>
            </div>
          </div>
        </motion.div>

        <p className="mt-10 max-w-[680px] text-[#A1A1AA] text-base leading-[1.6] italic">
          This illustrative dossier shows how the output turns customer data into a specific buyer your team can understand and write to.
        </p>
      </div>
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="font-mono-label text-[#71717A] text-[10px] mb-1.5">{label}</div>
      <div className="text-white text-[15px] font-medium">{value}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-white/[0.06]" />;
}

function Block({ title, children }) {
  return (
    <div>
      <div className="font-mono-label text-[#71717A] text-[10px] mb-2.5">{title}</div>
      <div className="text-[#D4D4D8] text-[15px] leading-[1.55]">{children}</div>
    </div>
  );
}

function TagRow({ words, muted = false }) {
  return (
    <div className="flex flex-wrap gap-2">
      {words.map((w) => (
        <span
          key={w}
          className={`inline-flex items-center px-2.5 py-1 rounded-md border text-[13px] transition-colors ${
            muted
              ? "bg-transparent border-white/[0.06] text-[#52525B] line-through decoration-[#52525B] decoration-1"
              : "bg-[#D4FF4A]/[0.08] border-[#D4FF4A]/25 text-[#D4FF4A]"
          }`}
        >
          {w}
        </span>
      ))}
    </div>
  );
}
